import { assertNever } from "@impero-com/common/utils/obj";

import type { PageState } from "api/generated/model";
import { Storage } from "utils/storage";

import { PageKey } from "./pageKey";

export function encodeKey(key: PageKey): string {
    switch (key.type) {
        case "ControlsAll":
        case "ControlsWeekly":
        case "ControlsMonthly":
        case "ControlsTwoMonths":
        case "ControlsQuarterly":
        case "ControlsHalfYearly":
        case "ControlsYearly":
        case "ControlsManual":
        case "Users":
        case "UserGroups":
        case "ControlPrograms":
        case "Tags":
        case "ReportsAll":
        case "ReportsStatus":
        case "ReportsClassification":
        case "ReportsWorkload":
        case "ReportsDatasheet":
        case "Dashboard":
            return key.type;
        case "ControlProgram":
            return `ControlProgram:${key.id}`;
        case "RiskMap":
            if (key.entityId !== undefined) {
                return `${key.type}:RiskDirectory:${key.riskDirectoryId}:Entity:${key.entityId}`;
            } else {
                return `${key.type}:RiskDirectory:${key.riskDirectoryId}`;
            }
        case "RiskDirectory":
            return `${key.type}:RiskDirectory:${key.riskDirectoryId}`;
        case "Activities": {
            const { scope } = key;
            switch (scope.type) {
                case "None":
                    return key.type;
                case "ControlProgram":
                    return `${key.type}:ControlProgram:${scope.controlProgramId}`;
                case "Control":
                    return `${key.type}:Control:${scope.controlId}`;
                default:
                    return assertNever(scope);
            }
        }
        case "DataSheetReport": {
            const scope = key.scope;
            switch (scope.type) {
                case "DataSheet":
                    return `${key.type}:DataSheet:${scope.dataSheetId}`;
                default:
                    return assertNever(scope.type);
            }
        }
        default:
            return assertNever(key);
    }
}

// NOTE: there is no runtime verification of type correctness at the moment.
// In the future we may want to use more advanced codecs, that performs runtime verification of type against JSON schema.
// See sc-20905.
export type PageStateStorage = Storage<
    "localStorage",
    "pageState",
    PageKey,
    PageState
>;
export const pageStateStorage: PageStateStorage = new Storage({
    type: "localStorage",
    namespace: "pageState",
    codecs: {
        encodeKey,
        encodeValue: (val: PageState) => JSON.stringify(val),
        decodeValue: (json: string) => JSON.parse(json) as PageState,
    },
});
